import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

const LoginPage = ({ defaultPanel = 'signin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendOTP, verifyOTP, initializeRecaptcha, clearRecaptcha, currentUser } = useAuth();

  const from = location.state?.from?.pathname || '/';

  const [isSignupPanel, setIsSignupPanel] = useState(defaultPanel === 'signup');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate(from, { replace: true });
    }
  }, [currentUser, navigate, from]);

  const [signinData, setSigninData] = useState({
    phoneNumber: ''
  });
  const [signinOtp, setSigninOtp] = useState('');
  const [signinConfirmation, setSigninConfirmation] = useState(null);
  const [showSigninOtpInput, setShowSigninOtpInput] = useState(false);

  const [signupData, setSignupData] = useState({
    name: '',
    phoneNumber: ''
  });

  const [signupOtp, setSignupOtp] = useState('');
  const [signupConfirmation, setSignupConfirmation] = useState(null);
  const [showSignupOtpInput, setShowSignupOtpInput] = useState(false);


  useEffect(() => {
    setIsSignupPanel(defaultPanel === 'signup');
  }, [defaultPanel]);

  useEffect(() => {
    return () => {
      clearRecaptcha();
    };
  }, [clearRecaptcha]);

  const resetSignupFlow = () => {
    setShowSignupOtpInput(false);
    setSignupOtp('');
    setSignupConfirmation(null);
  };

  const switchToSignup = () => {
    setIsSignupPanel(true);
    setError('');
  };

  const switchToSignin = () => {
    setIsSignupPanel(false);
    setError('');
    resetSignupFlow();
  };

  const formatPhoneNumber = (rawPhoneNumber) => {
    let phoneNumber = rawPhoneNumber.trim().replace(/\s+/g, '');
    
    // If it already has +, just return it
    if (phoneNumber.startsWith('+')) {
      return phoneNumber;
    }
    
    // Handle Indian numbers (10 digits or 12 digits starting with 91)
    if (phoneNumber.length === 12 && phoneNumber.startsWith('91')) {
      return `+${phoneNumber}`;
    }
    
    // Default to +91 for 10-digit numbers or others
    return `+91${phoneNumber.replace(/^0/, '')}`;
  };

  const onSigninInput = (e) => {
    const { name, value } = e.target;
    setSigninData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const onSignupInput = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSigninSendOtp = async (e) => {
    e.preventDefault();

    if (!signinData.phoneNumber) {
      setError('Please enter your phone number');
      return;
    }

    const phoneNumber = formatPhoneNumber(signinData.phoneNumber);

    setLoading(true);
    setError('');

    try {
      const confirmation = await sendOTP(phoneNumber);
      setSigninConfirmation(confirmation);
      setShowSigninOtpInput(true);
    } catch (otpError) {
      if (otpError.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a while before trying again, or use a Firebase test phone number.');
      } else {
        setError(`Failed to send OTP. Please try again: ${otpError.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSigninVerifyOtp = async (e) => {
    e.preventDefault();

    if (!signinOtp || signinOtp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyOTP(signinConfirmation, signinOtp);
      navigate(from, { replace: true });
    } catch (otpError) {
      switch (otpError.code) {
        case 'auth/invalid-verification-code':
          setError('Invalid OTP. Please check and try again.');
          break;
        case 'auth/code-expired':
          setError('OTP expired. Please send OTP again.');
          break;
        default:
          setError('OTP verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSendOtp = async (e) => {
    e.preventDefault();

    if (!signupData.name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!signupData.phoneNumber) {
      setError('Please enter your phone number');
      return;
    }

    const phoneNumber = formatPhoneNumber(signupData.phoneNumber);

    setLoading(true);
    setError('');

    try {
      const confirmation = await sendOTP(phoneNumber);
      setSignupConfirmation(confirmation);
      setShowSignupOtpInput(true);
    } catch (otpError) {
      if (otpError.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a while before trying again, or use a Firebase test phone number.');
      } else {
        setError(`Failed to send OTP. Please try again: ${otpError.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignupVerifyOtp = async (e) => {
    e.preventDefault();

    if (!signupOtp || signupOtp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyOTP(signupConfirmation, signupOtp, {
        name: signupData.name,
        email: '',
        phoneNumber: signupData.phoneNumber
      });
      navigate(from, { replace: true });
    } catch (otpError) {
      switch (otpError.code) {
        case 'auth/invalid-verification-code':
          setError('Invalid OTP. Please check and try again.');
          break;
        case 'auth/code-expired':
          setError('OTP expired. Please send OTP again.');
          break;
        default:
          setError('OTP verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-header-hero">
        <div className="breadcrumb">
          <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">Login</span>
        </div>
        <h1 className="auth-title">Login</h1>
      </div>
      <div className={`auth-main ${isSignupPanel ? 'is-signup' : ''}`}>
        <div className="mobile-auth-toggle">
          <button
            className={`mobile-auth-btn ${!isSignupPanel ? 'active' : ''}`}
            type="button"
            onClick={switchToSignin}
          >
            SIGN IN
          </button>
          <button
            className={`mobile-auth-btn ${isSignupPanel ? 'active' : ''}`}
            type="button"
            onClick={switchToSignup}
          >
            SIGN UP
          </button>
        </div>

        <div className="mobile-auth-copy">
          <h3>{isSignupPanel ? 'Hello Friend!' : 'Welcome Back!'}</h3>
          <p>
            {isSignupPanel
              ? 'Enter your details and create your Nexawave account with OTP.'
              : 'To stay connected with us, sign in with your mobile number and OTP.'}
          </p>
        </div>

        <div className="container a-container">
          {!showSignupOtpInput ? (
            <form className="form" onSubmit={handleSignupSendOtp}>
              <h2 className="form_title title">Create Account</h2>
              <span className="form__span">Use phone number for registration</span>
              <input
                className="form__input"
                type="text"
                name="name"
                placeholder="Name"
                value={signupData.name}
                onChange={onSignupInput}
                required
              />
              <input
                className="form__input"
                type="tel"
                name="phoneNumber"
                placeholder="Phone Number"
                value={signupData.phoneNumber}
                onChange={onSignupInput}
                required
              />
              <button className="form__button button submit" type="submit" disabled={loading}>
                {loading ? 'SENDING...' : 'SEND OTP'}
              </button>
            </form>
          ) : (
            <form className="form" onSubmit={handleSignupVerifyOtp}>
              <h2 className="form_title title">Verify OTP</h2>
              <span className="form__span">Enter the 6-digit code sent to your phone</span>
              <input
                className="form__input"
                type="text"
                placeholder="6-digit OTP"
                value={signupOtp}
                onChange={(e) => setSignupOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
              />
              <button className="form__button button submit" type="submit" disabled={loading}>
                {loading ? 'VERIFYING...' : 'VERIFY & SIGN UP'}
              </button>
              <button
                className="form__button button ghost-btn"
                type="button"
                onClick={resetSignupFlow}
                disabled={loading}
              >
                CHANGE NUMBER
              </button>
            </form>
          )}
        </div>

        <div className="container b-container">
          {!showSigninOtpInput ? (
            <form className="form" onSubmit={handleSigninSendOtp}>
              <h2 className="form_title title">Sign In</h2>
              <span className="form__span">Use your mobile number</span>
              <input
                className="form__input"
                type="tel"
                name="phoneNumber"
                placeholder="Phone Number"
                value={signinData.phoneNumber}
                onChange={onSigninInput}
                required
              />
              <button className="form__button button submit" type="submit" disabled={loading}>
                {loading ? 'SENDING...' : 'SEND OTP'}
              </button>
            </form>
          ) : (
            <form className="form" onSubmit={handleSigninVerifyOtp}>
              <h2 className="form_title title">Verify OTP</h2>
              <span className="form__span">Enter the 6-digit code sent to your phone</span>
              <input
                className="form__input"
                type="text"
                placeholder="6-digit OTP"
                value={signinOtp}
                onChange={(e) => setSigninOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
              />
              <button className="form__button button submit" type="submit" disabled={loading}>
                {loading ? 'VERIFYING...' : 'VERIFY & SIGN IN'}
              </button>
              <button
                className="form__button button ghost-btn"
                type="button"
                onClick={() => {
                  setShowSigninOtpInput(false);
                  setSigninOtp('');
                  setSigninConfirmation(null);
                }}
                disabled={loading}
              >
                CHANGE NUMBER
              </button>
            </form>
          )}
        </div>

        <div className="switch">
          <div className="switch__circle"></div>
          <div className="switch__circle switch__circle--t"></div>

          <div className={`switch__container ${isSignupPanel ? 'is-hidden' : ''}`}>
            <h2 className="switch__title title">Hello Friend!</h2>
            <p className="switch__description description">
              Enter your details and create your Nexawave account with OTP.
            </p>
            <button className="switch__button button" type="button" onClick={switchToSignup}>
              SIGN UP
            </button>
          </div>

          <div className={`switch__container ${isSignupPanel ? '' : 'is-hidden'}`}>
            <h2 className="switch__title title">Welcome Back!</h2>
            <p className="switch__description description">
              To stay connected with us, please sign in with your personal info.
            </p>
            <button className="switch__button button" type="button" onClick={switchToSignin}>
              SIGN IN
            </button>
          </div>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}
    </div>
  );
};

export default LoginPage;
